import { ColumnDef } from "@tanstack/react-table";

import type { IUnlockDetails, StatusBadge } from "@/@types/unlock-request";
import { Badge } from "@/components/ui/badge";
import { SITUATION_STATUS } from "@/constants/access-request";
import {
  UNLOCK_ENTITY_LABEL,
  UNLOCK_STATUS_BADGE,
  UNLOCK_TAB_LABELS,
  UNLOCK_UNKNOWN_STATUS,
} from "@/constants/messages";
import { UNLOCK_EMPTY_CELL } from "@/constants/unlock-request";
import { AccessRequestDTO } from "@/services/dtos/accessRequest.dto";
import { formatUnlockCpf, formatUnlockDate } from "@/utils/unlock-request";

import { readUnlockDetails } from "./access-details";
import { SituationCell } from "./situation-cell";

export const unlockColumns: ColumnDef<AccessRequestDTO>[] = [
  {
    id: "name",
    accessorKey: "name",
    header: UNLOCK_TAB_LABELS.COLUMN_NAME,
  },
  {
    id: "entityType",
    header: UNLOCK_TAB_LABELS.COLUMN_TYPE,
    cell: ({ row }): string => {
      const unlock: IUnlockDetails | null = readUnlockDetails(row.original);
      if (!unlock) return UNLOCK_EMPTY_CELL;

      return UNLOCK_ENTITY_LABEL[unlock.entityType] ?? UNLOCK_EMPTY_CELL;
    },
  },
  {
    id: "store",
    accessorKey: "store",
    header: UNLOCK_TAB_LABELS.COLUMN_STORE,
    cell: ({ row }): string => UNLOCK_TAB_LABELS.STORE(row.original.store),
  },
  {
    id: "cpf",
    accessorKey: "cpf",
    header: UNLOCK_TAB_LABELS.COLUMN_CPF,
    cell: ({ row }): string => formatUnlockCpf(row.original.cpf),
  },
  {
    id: "situation",
    header: UNLOCK_TAB_LABELS.COLUMN_SITUATION,
    cell: ({ row }): JSX.Element => {
      const unlock: IUnlockDetails | null = readUnlockDetails(row.original);

      return (
        <SituationCell
          situation={unlock?.seniorSituation ?? null}
          situationStatus={
            unlock?.seniorSituationStatus ?? SITUATION_STATUS.UNAVAILABLE
          }
        />
      );
    },
  },
  {
    id: "status",
    accessorKey: "status",
    header: UNLOCK_TAB_LABELS.COLUMN_STATUS,
    cell: ({ row }): JSX.Element => {
      const badge: StatusBadge =
        UNLOCK_STATUS_BADGE[row.original.status] ?? UNLOCK_UNKNOWN_STATUS;

      return <Badge variant={badge.variant}>{badge.label}</Badge>;
    },
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    header: UNLOCK_TAB_LABELS.COLUMN_CREATED_AT,
    cell: ({ row }): string => formatUnlockDate(row.original.created_at),
  },
];
